import { pxToEm } from 'util/pxToEm';
import { Badge, IconButton } from '@mui/material';
import React, { ReactElement, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { NotificationIcon } from 'components/icons';
import { useNotificationsApi } from 'services/api/hooks/useNotificationsApi.hook';
import useSWR from 'swr';
import { NotificationDrawer } from '../NotificationDrawer';

export const NotificationButton = (): ReactElement => {
  const router = useRouter();
  const [isOpen, setOpen] = useState(false);

  // Reopen notification drawer when feedback drawer closes if it was opened from notification
  useEffect(() => {
    const fromNotification = router.query.fromNotification === 'true';
    const hasFeedback = !!router.query.feedback;

    // If feedback drawer closes (no feedback param) but we came from notification, reopen notification drawer
    if (!hasFeedback && fromNotification && !isOpen) {
      setOpen(true);
      // Clean up the fromNotification param
      const { fromNotification: _, ...restQuery } = router.query;
      void router.replace(
        {
          pathname: router.pathname,
          query: restQuery,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [router.query.feedback, router.query.fromNotification, isOpen, router]);
  const notificationsApi = useNotificationsApi();

  const { data: unreadCountData } = useSWR(
    'notifications-unread-count',
    async () => {
      const result = await notificationsApi.getUnreadCount();
      if (!result.success) return { count: 0 };
      return result.value;
    },
    {
      revalidateOnFocus: true,
      refreshInterval: 30000, // Refresh every 30 seconds
    },
  );

  const unreadCount = unreadCountData?.count || 0;

  return (
    <>
      <Badge
        badgeContent={unreadCount > 0 ? unreadCount : undefined}
        sx={{
          '& .MuiBadge-badge': {
            right: 0,
            top: 4,
            width: 24,
            height: 24,
            border: '1px solid #fff',
            borderRadius: '50%',
            background: '#FF385C',
            color: '#fff',
            fontSize: pxToEm(12),
            fontWeight: 600,
          },
        }}
      >
        <IconButton
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            background: '#fff',
            color: theme => theme.palette.primary.main,
            fontWeight: 'bold',
            '&:hover': {
              background: '#fff',
            },
            fontSize: pxToEm(20),
          }}
          onClick={() => setOpen(true)}
          aria-label="Notifications"
        >
          <NotificationIcon
            sx={{
              fontSize: pxToEm(20),
              color: '#23305F',
            }}
          />
        </IconButton>
      </Badge>
      <NotificationDrawer
        open={isOpen}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

